import type {
  AdminUploadCsvRow,
  AdminUserUploadCsv,
  AdminUser,
  AdminUserBase,
  AdminUserUpdate,
  AdminUserDestroy,
} from 'src/models/user';
import { type GroupInfoResponse, toGroupInfo } from 'src/models/apis/user/userResponse';

export interface AdminUserBaseResponse {
  id: number;
  username: string;
  display_name: string;
  role: number;
  group_id: number;
  default_page: string;
  group_info: GroupInfoResponse;
}

export interface AdminUserUpdateResponse extends AdminUserBaseResponse {
  active: number;
  timezone: string;
}

// destroy は更新後と同一形状のユーザーレコードを返す
export type AdminUserDestroyResponse = AdminUserUpdateResponse;

export interface AdminUserResponse extends AdminUserUpdateResponse {
  g2disp_order: number;
  g3disp_order: number;
}

export type AdminUserIndexResponse = AdminUserResponse[];

export interface AdminUploadCsvRowResponse {
  data_num: number;
  username: string;
  display_name: string;
  group_id: string;
  role: string;
  password: string;
  id?: number;
}

export interface AdminUploadCsvSuccessResponse {
  row: AdminUploadCsvRowResponse;
}

export interface AdminUploadCsvFailureResponse {
  row: AdminUploadCsvRowResponse;
  reasons: string[];
}

export interface AdminUserUploadCsvResponse {
  created: AdminUploadCsvSuccessResponse[];
  updated: AdminUploadCsvSuccessResponse[];
  failures: AdminUploadCsvFailureResponse[];
}

export const toAdminUploadCsvRow = (response: AdminUploadCsvRowResponse): AdminUploadCsvRow => ({
  dataNum: response.data_num,
  username: response.username,
  displayName: response.display_name,
  groupId: response.group_id,
  role: response.role,
  password: response.password,
  id: response.id,
});

export const toAdminUserUploadCsv = (response: AdminUserUploadCsvResponse): AdminUserUploadCsv => ({
  created: response.created.map(success => ({ row: toAdminUploadCsvRow(success.row) })),
  updated: response.updated.map(success => ({ row: toAdminUploadCsvRow(success.row) })),
  failures: response.failures.map(failure => ({
    row: toAdminUploadCsvRow(failure.row),
    reasons: failure.reasons,
  })),
});

export const toAdminUser = (response: AdminUserResponse): AdminUser => ({
  id: response.id,
  username: response.username,
  displayName: response.display_name,
  role: response.role,
  groupId: response.group_id,
  active: response.active,
  timezone: response.timezone,
  defaultPage: response.default_page,
  groupInfo: toGroupInfo(response.group_info),
  g2dispOrder: response.g2disp_order,
  g3dispOrder: response.g3disp_order,
});

export const toAdminUserBase = (response: AdminUserBaseResponse): AdminUserBase => ({
  id: response.id,
  username: response.username,
  displayName: response.display_name,
  role: response.role,
  groupId: response.group_id,
  defaultPage: response.default_page,
  groupInfo: toGroupInfo(response.group_info),
});

export const toAdminUserUpdate = (response: AdminUserUpdateResponse): AdminUserUpdate => ({
  ...toAdminUserBase(response),
  active: response.active,
  timezone: response.timezone,
});

export const toAdminUserDestroy = (response: AdminUserDestroyResponse): AdminUserDestroy => ({
  ...toAdminUserBase(response),
  active: response.active,
  timezone: response.timezone,
});
